import {useTranslation} from "react-i18next";
import InitContext from "../pages/InitContext";
import React, {useContext, useEffect, useState} from "react";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import PatientGenes from "./PatientGenes";
import {Consts} from "../pages/consts";
import InitFooter from "../patientInit/InitFooter";
import CreateNewGeneView from "./CreateNewGeneView";
import NewPgxTest from "./NewPgxTest";
import PrintDgis from "./PrintDgis";

const PGx = ({init=false, printView = false, onHandleBack= null, onFinishInit = null,
               onHandleNext = null, patientId}) => {
  const {t} = useTranslation();
  const context = useContext(InitContext);
  const [createNewDialogOpened, setCreateNewDialogOpened] = useState(false);
  const [newGenesSelected, setNewGenesSelected] = useState(false);
  const [newPgx, setNewPgx] = useState(null);
  const [patGenes, setPatGenes] = useState([]);
  const [availableGeneNames, setAvailableGeneNames] = useState([]);

  const {
    loading,
    error,
    clearError,
    getDbGenes,
    saveDbGeneTest,
  } = useMmcService();

  useEffect(() => {
    if (patGenes === undefined || patGenes === null || patGenes?.length === 0) {
      fetchDbGenes().then();
    }
  }, []);

  const fetchDbGenes = async () => {
    clearError();

    try {
      const genesInDb = await getDbGenes(context?.token, patientId);
      setPatGenes(genesInDb);

      if (!availableGeneNames?.length) {
        setAvailableGeneNames(Consts.ALL_GENE_NAMES);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveNewGeneTest = async () => {
    clearError();

    saveDbGeneTest(context?.token, newPgx, patientId).then(() => {
      clearError();

      getDbGenes(context?.token, patientId).then((genesInDb) => {
        setPatGenes(genesInDb);
        setNewPgx(null);
        setCreateNewDialogOpened(false);
        setNewGenesSelected(false);
        setAvailableGeneNames(Consts.ALL_GENE_NAMES);
      });
    });
  };

  const handleSetPatGenes = (newGenes) => {
    setPatGenes(newGenes);
  }

  const handleInitNewTest = (newGenes) => {
    setNewPgx((prev) => ({...prev, genes: newGenes}));
  }

  const showGenesAfterStoringNewTest = (newGenes) => {
    setNewGenesSelected(false);
    setCreateNewDialogOpened(false)
    setNewPgx((prev) => ({...prev, genes: newGenes}));
    setPatGenes(newGenes);
  }

  const handleCreateNewTest = () => {
    setNewPgx(null);
    setCreateNewDialogOpened(true);
    setNewGenesSelected(false);
    setAvailableGeneNames(Consts.ALL_GENE_NAMES);
  }

  const handleCancel = () => {
    fetchDbGenes().then(() => {
        setNewPgx(null);
        setCreateNewDialogOpened(false);
        setNewGenesSelected(false);
        setAvailableGeneNames(Consts.ALL_GENE_NAMES);
    });
  }

  const errorMessage = error && <ErrorMessage message={ t(error?.includes(Consts.POST)
    ? "error_by_saving_pgx_test_data"
    : "error_by_getting_pgx_test_data") }/>;

  const spinner = !printView && loading && <Spinner/>;

  const handlePgxNextLocally = () => {
    onHandleNext();
  }

  return (
    <div id="pgx-parent">
      { printView ? (
        <div className="w3-container">
          { patGenes?.length > 0
            ?
            <>
              <PatientGenes patGenes={ patGenes } setPatGenes={ handleSetPatGenes } init={ init }
                            printView={ printView } patientId={ patientId } />
              <PrintDgis patGenes={ patGenes } />
            </>
            : <label>{ t("not_available") }</label>
          }
        </div>
      ) : (
        <div className="w3-display-container content-container">
          { !init &&
            <div style={ {width: '100%', textAlign: 'right', paddingRight: "20px"} }>
              <label>
                { t("username") }: { context?.patient?.login }, { " " }
                { t("role") }: { t("patient").toLowerCase() }
              </label>
            </div>
          }
          <div className="w3-display-middle form-display">
            <div className="w3-panel w3-card-4 record-panel" style={ {backgroundColor: init ? "#e9c67d" : "#CFE6EF"} }>
              <div className="w3-row w3-margin">
                <header className="w3-col s8 w3-container w3-center">
                  <h2>{ t("pharmacogenetic_test_results") }</h2>
                </header>
                { (!createNewDialogOpened && !newGenesSelected) &&
                  <button
                    className="w3-col s3 w3-btn w3-round-xlarge w3-theme-d5 w3-margin-top"
                    onClick={ handleCreateNewTest }
                  >
                    { t("create_new") }
                  </button>
                }
              </div>
              { errorMessage }
              { spinner }
              { createNewDialogOpened &&
                <CreateNewGeneView newPgx={ newPgx } setNewPgx={ setNewPgx } init={ init }
                                   availableGeneNames={ availableGeneNames }
                                   setAvailableGeneNames={ setAvailableGeneNames }
                                   onHandleInitNewTest={ handleInitNewTest }
                                   onHandleNextClick={ () => setCreateNewDialogOpened(false) }
                                   onHandleCancel={ handleCancel }/>
              }
              { !createNewDialogOpened && newPgx !== null && // shows a view with rows of a new PGx (not yet saved, no ID)
                <NewPgxTest newPgx={ newPgx } setNewPgx={ setNewPgx } init={ init }
                            handleSetPatGenes={ showGenesAfterStoringNewTest } patientId={ patientId }
                            handleSaveNewGeneTest={ handleSaveNewGeneTest } handleCancel={ handleCancel }/>
              }
              { !createNewDialogOpened && newPgx === null && // shows PGx with trash icons (saved, with ID)
                <div className="w3-container">
                  <PatientGenes patGenes={ patGenes } setPatGenes={ handleSetPatGenes } init={ init }
                                patientId={ patientId } />
                </div>
              }
              { init && !createNewDialogOpened && !newGenesSelected
                ?
                <div className="w3-margin-top">
                  <InitFooter parameter="pgx" onHandleBack={ onHandleBack } myPatient={ context?.patient }
                              onFinishInit={ onFinishInit } onHandleNext={ handlePgxNextLocally }
                              nextButtonEnabled={ true }/>
                </div>
                :
                <footer className="w3-container w3-center w3-padding-small">
                  <div className="w3-left w3-padding-small"></div>
                </footer>
              }
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};

export default PGx;
